// @ts-nocheck
/** *****
 * Store Custom Code:
 * For updates, compare with: modules\content-library\src\Components\ProductPartNumbers.tsx
 * ****** */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // Store Custom Code
    glNumber?: string;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // Store Custom Code
    glNumberGridItem?: GridItemProps;
    glNumberLabelText?: TypographyPresentationProps;
    glNumberValueText?: TypographyPresentationProps;
}

/* Store Custom Code - Start */
const gridItemProps: GridItemProps = {
    css: css`
        font-size: ${x => x.theme.design.typography.size.smaller};
    `,
};

const labelProps: TypographyPresentationProps = {
    css: css`
        font-weight: ${x => x.theme.design.typography.weight.base};
        margin-right: 0.25em;
    `,
};

const valueProps: TypographyPresentationProps = {
    css: css`
        font-weight: ${x => x.theme.design.typography.weight.medium};
    `,
};
/* Store Custom Code - End */

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    /* Store Custom Code - Start */
    container: {
        gap: 5,
        css: css`
            margin-bottom: ${pxToRem(10)};
        `,
    },
    erpNumberGridItem: gridItemProps,
    erpNumberLabelText: labelProps,
    erpNumberValueText: valueProps,
    customerNameGridItem: gridItemProps,
    customerNameLabelText: labelProps,
    customerNameValueText: valueProps,
    manufacturerItemGridItem: gridItemProps,
    manufacturerItemLabelText: labelProps,
    manufacturerItemValueText: valueProps,
    glNumberGridItem: gridItemProps,
    glNumberLabelText: labelProps,
    glNumberValueText: valueProps,
    /* Store Custom Code - End */
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    // Store Custom Code
    glNumber,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            {showProductNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {/* Store Custom Code - Start */}
                    {showLabel && (
                        <Typography {...styles.erpNumberLabelText}>
                            {translate("Part #")}{" "}
                            <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                                {productNumber}
                            </Typography>
                        </Typography>
                    )}
                    {/* Store Custom Code - End */}
                    {!showLabel && (
                        <>
                            <VisuallyHidden>
                                <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                            </VisuallyHidden>
                            {/* Store Custom Code - Start */}
                            <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                                {productNumber}
                            </Typography>
                            {/* Store Custom Code - End */}
                        </>
                    )}
                </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </GridItem>
            )}
            {/* Store Custom Code - Start */}
            {glNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.glNumberGridItem}>
                    {showLabel && <Typography {...styles.glNumberLabelText}>{translate("GL #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.glNumberLabelText}>{translate("GL #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.glNumberValueText}>{glNumber}</Typography>
                </GridItem>
            )}
            {/* Store Custom Code - End */}
        </GridContainer>
    );
};

export default ProductPartNumbers;
